import { ref, onMounted, onUnmounted } from 'vue'
import { Network } from '@capacitor/network'
import { Capacitor } from '@capacitor/core'

/**
 * Tipos de status de rede
 */
export type NetworkStatus = 'online' | 'offline' | 'unknown'

/**
 * Tipo de conexão de rede
 */
export type ConnectionType =
  | 'wifi'
  | 'cellular'
  | 'none'
  | 'unknown'
  | 'ethernet'
  | 'bluetooth'
  | 'vpn'
  | 'other'

/**
 * Interface para informações de rede
 */
export interface NetworkInfo {
  connected: boolean
  connectionType: ConnectionType
  status: NetworkStatus
  effectiveType?: string // slow-2g, 2g, 3g, 4g
}

/**
 * Estado global da rede
 */
const networkState = ref<NetworkInfo>({
  connected: true,
  connectionType: 'unknown',
  status: 'online',
})

/**
 * Eventos de mudança de rede
 */
const networkListeners = new Set<(status: NetworkInfo) => void>()

/**
 * Detecta se está em app nativo
 */
function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Obtém informações da rede de forma nativa (Capacitor)
 */
async function getNativeNetworkInfo(): Promise<NetworkInfo> {
  try {
    const status = await Network.getStatus()
    return {
      connected: status.connected,
      connectionType: status.connectionType as ConnectionType,
      status: status.connected ? 'online' : 'offline',
      effectiveType: (status as any).networkDirection,
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Network] Erro ao obter status nativo:', error)
    }
    return getWebNetworkInfo()
  }
}

/**
 * Obtém informações da rede na web (navigator.onLine)
 */
function getWebNetworkInfo(): NetworkInfo {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : true
  return {
    connected: online,
    connectionType: 'unknown',
    status: online ? 'online' : 'offline',
  }
}

/**
 * Atualiza o estado da rede e notifica listeners
 */
function updateNetworkState(info: NetworkInfo) {
  networkState.value = info
  networkListeners.forEach((listener) => listener(info))
}

/**
 * Inicia o monitoramento da rede
 */
async function startNetworkMonitoring() {
  if (isNativeApp()) {
    // Monitoramento nativo com Capacitor Network
    const initialStatus = await getNativeNetworkInfo()
    updateNetworkState(initialStatus)

    // Listener para mudanças de rede
    const handler = Network.addListener('networkStatusChange', (status) => {
      updateNetworkState({
        connected: status.connected,
        connectionType: status.connectionType as ConnectionType,
        status: status.connected ? 'online' : 'offline',
        effectiveType: (status as any).networkDirection,
      })
    })

    // Retornar função de cleanup
    return async () => {
      const listener = await handler
      listener.remove()
    }
  } else {
    // Monitoramento na web
    updateNetworkState(getWebNetworkInfo())

    const handleOnline = () => updateNetworkState(getWebNetworkInfo())
    const handleOffline = () => updateNetworkState(getWebNetworkInfo())

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Retornar função de cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }
}

/**
 * Composable para uso em componentes
 */
export function useNetwork() {
  let stopMonitoring: (() => void) | undefined

  onMounted(async () => {
    stopMonitoring = await startNetworkMonitoring()
  })

  onUnmounted(() => {
    stopMonitoring?.()
  })

  return {
    network: networkState,
    isOnline: () => networkState.value.connected,
    isOffline: () => !networkState.value.connected,
    getConnectionType: () => networkState.value.connectionType,
    isSlowConnection: () => {
      const type = networkState.value.connectionType
      return type === 'cellular' || type === 'none'
    },
    isWifiConnection: () => networkState.value.connectionType === 'wifi',
    onNetworkChange: (callback: (status: NetworkInfo) => void) => {
      networkListeners.add(callback)
      // Retornar função de unsubscribe
      return () => networkListeners.delete(callback)
    },
  }
}

/**
 * Funções síncronas para uso em interceptors
 */
export const networkService = {
  /**
   * Verifica se está online de forma síncrona
   */
  isOnline(): boolean {
    return networkState.value.connected
  },

  /**
   * Verifica se está offline de forma síncrona
   */
  isOffline(): boolean {
    return !networkState.value.connected
  },

  /**
   * Obtém o estado atual da rede
   */
  getState(): NetworkInfo {
    return networkState.value
  },

  /**
   * Obtém o tipo de conexão
   */
  getConnectionType(): ConnectionType {
    return networkState.value.connectionType
  },

  /**
   * Verifica se é uma conexão lenta (celular, etc)
   */
  isSlowConnection(): boolean {
    const type = networkState.value.connectionType
    return type === 'cellular' || type === 'none'
  },

  /**
   * Inicializa o monitoramento (chamar na inicialização do app)
   */
  async initialize() {
    const info = isNativeApp()
      ? await getNativeNetworkInfo()
      : getWebNetworkInfo()
    updateNetworkState(info)
    await startNetworkMonitoring()
  },

  /**
   * Adiciona um listener para mudanças de rede
   */
  onChange(callback: (status: NetworkInfo) => void): () => void {
    networkListeners.add(callback)
    return () => networkListeners.delete(callback)
  },
}

/**
 * Inicializa o serviço automaticamente
 */
export async function initNetworkService() {
  await networkService.initialize()
}
